import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, TrendingUp, Users, FileText, Calendar, Filter, X } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { SearchLog } from '../../types';

interface Filters {
  startDate?: string;
  endDate?: string;
  hasResults?: boolean;
  minResults?: number;
  maxResults?: number;
  searchQuery?: string;
  ipAddress?: string;
}

export const SearchAnalytics: React.FC = () => {
  const [logs, setLogs] = useState<SearchLog[]>([]);
  const [analytics, setAnalytics] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [analyticsFilters, setAnalyticsFilters] = useState<{ startDate?: string; endDate?: string }>({});

  useEffect(() => {
    loadAnalytics();
    loadLogs();
  }, [currentPage, filters, analyticsFilters]);

  const loadAnalytics = async () => {
    try {
      const data = await adminAPI.getSearchAnalytics(analyticsFilters);
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to load analytics');
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getSearchLogs(currentPage, 20, filters);
      setLogs(data.logs);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Failed to load search logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Filters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleAnalyticsFilterChange = (newFilters: { startDate?: string; endDate?: string }) => {
    setAnalyticsFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setAnalyticsFilters({});
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.keys(filters).length > 0 || Object.keys(analyticsFilters).length > 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatSearchParams = (params: any) => {
    return Object.entries(params)
      .filter(([key, value]) => value && !key.includes('_tol'))
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Search Analytics</h1>
          <div className="flex space-x-2">
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-3 py-2 text-sm border rounded-md ${
                showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4 mr-1" />
              Filters
            </button>
          </div>
        </div>
      </motion.div>

      {/* Filter Panel */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-8 bg-white rounded-lg shadow-lg p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Analytics Date Range */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Analytics Date Range</h3>
              <div className="space-y-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={analyticsFilters.startDate || ''}
                    onChange={(e) => handleAnalyticsFilterChange({
                      ...analyticsFilters,
                      startDate: e.target.value || undefined
                    })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={analyticsFilters.endDate || ''}
                    onChange={(e) => handleAnalyticsFilterChange({
                      ...analyticsFilters,
                      endDate: e.target.value || undefined
                    })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Log Filters */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Search Log Filters</h3>
              <div className="space-y-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Log Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate || ''}
                    onChange={(e) => handleFilterChange({
                      ...filters,
                      startDate: e.target.value || undefined
                    })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Log End Date</label>
                  <input
                    type="date"
                    value={filters.endDate || ''}
                    onChange={(e) => handleFilterChange({
                      ...filters,
                      endDate: e.target.value || undefined
                    })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Results Filter</label>
                  <select
                    value={filters.hasResults === undefined ? '' : filters.hasResults.toString()}
                    onChange={(e) => handleFilterChange({
                      ...filters,
                      hasResults: e.target.value === '' ? undefined : e.target.value === 'true'
                    })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All</option>
                    <option value="true">Has Results</option>
                    <option value="false">No Results</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Search Criteria */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Search Criteria</h3>
              <div className="space-y-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Search Query</label>
                  <input
                    type="text"
                    placeholder="Search in queries..."
                    value={filters.searchQuery || ''}
                    onChange={(e) => handleFilterChange({
                      ...filters,
                      searchQuery: e.target.value || undefined
                    })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">IP Address</label>
                  <input
                    type="text"
                    placeholder="Filter by IP..."
                    value={filters.ipAddress || ''}
                    onChange={(e) => handleFilterChange({
                      ...filters,
                      ipAddress: e.target.value || undefined
                    })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Min Results</label>
                    <input
                      type="number"
                      min="0"
                      value={filters.minResults || ''}
                      onChange={(e) => handleFilterChange({
                        ...filters,
                        minResults: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Results</label>
                    <input
                      type="number"
                      min="0"
                      value={filters.maxResults || ''}
                      onChange={(e) => handleFilterChange({
                        ...filters,
                        maxResults: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="flex items-center">
            <Search className="h-8 w-8 text-[#63B56F]" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                {analytics.appliedFilters ? 'Filtered Searches' : 'Total Searches'}
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics.appliedFilters ? analytics.customRangeSearches : analytics.totalSearches || 0}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Week</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.weeklySearches || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.monthlySearches || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-red-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">No Results</p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics.appliedFilters ? analytics.customRangeNoResults : analytics.noResultSearches || 0}
              </p>
              <p className="text-xs text-gray-500">
                {analytics.noResultPercentage?.toFixed(1)}%
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="flex items-center">
            <Users className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Results</p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics.avgResultsPerSearch?.toFixed(1) || '0.0'}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search Logs */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Search Logs</h2>
            {hasActiveFilters && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Filtered Results
              </span>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Search Query
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Search Parameters
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Results
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Email
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    Loading search logs...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    No search logs found {hasActiveFilters ? 'matching the current filters' : ''}
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                      <div className="truncate" title={log.searchQuery}>
                        {log.searchQuery || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                      <div className="truncate" title={formatSearchParams(log.searchParams)}>
                        {formatSearchParams(log.searchParams) || 'No parameters'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        log.resultsCount > 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {log.resultsCount} results
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.ipAddress || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.userEmail || 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
